import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { BooleanRenderer } from '../../../components/AgTable/cell-renderers/BooleanRenderer';
import { LongTextCellRenderer } from '../../../components/AgTable/cell-renderers/LongTextCellRenderer';
import { RoundRenderer } from '../../../components/AgTable/cell-renderers/RoundRenderer';
import { SymbolRenderer } from '../../../components/AgTable/cell-renderers/SymbolRenderer';
import { TransactionType } from '../../../data-models/transaction-type.data-model';
import { ITransactionDataModel } from '../../../data-models/transaction.data-model';
import { transactionTypesByIdMapAtom, usersByEmailMapAtom } from '../../../services/state/AppConfigStateJ';
import { TransactionHistoryTableData } from '../../../types';
import { CurrencyRenderer } from '../components/CurrencyRenderer';
import { DateFormatter } from '../components/DateFormatter';
import { NumberRenderer } from '../components/NumberRenderer';
import { PercentageFormatter } from '../components/PercentageFormatter';

type RowParams = CustomCellRendererProps<TransactionHistoryTableData>;

function isITransactionDataModel(
  transaction: TransactionHistoryTableData
): transaction is ITransactionDataModel {
  return (
    (transaction as ITransactionDataModel).amountNormalized !== undefined &&
    (transaction as ITransactionDataModel).noOfSharesNormalized !== undefined
  );
}

function dateAccessor(dateString: string | null | undefined) {
  return dateString ? new Date(dateString) : null;
}

const fundCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Fund',
  valueGetter: (p: ValueGetterParams<TransactionHistoryTableData>) => p.data?.fund?.name,
  colId: 'fundName',
};
const transactionDateCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Transaction Date',
  valueGetter: (p: ValueGetterParams<TransactionHistoryTableData>) => dateAccessor(p.data?.transactionDate),
  cellRenderer: DateFormatter,
  colId: 'transactionDate',
};
const paymentDateCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Payment Date',
  valueGetter: (p: ValueGetterParams<TransactionHistoryTableData>) => dateAccessor(p.data?.transactionDate),
  cellRenderer: DateFormatter,
  colId: 'transactionDate',
};
const roundCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Stage',
  field: 'round',
  cellRenderer: RoundRenderer,
  colId: 'round',
};
const convertedRoundCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Converted Stage',
  field: 'round',
  cellRenderer: RoundRenderer,
  colId: 'convertedRound',
};
const PPSCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'PPS',
  field: 'pricePerShare',
  cellRenderer: (p: RowParams) => <CurrencyRenderer value={p.value} currencyCode={p.data?.currency?.code} />,
};
const newSharesPurchasedCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'New Shares Purchased',
  valueGetter: (p: ValueGetterParams<TransactionHistoryTableData>) => {
    if (!p.data) {
      return null;
    }
    return isITransactionDataModel(p.data) ? p.data.noOfSharesNormalized : p.data.noOfShares;
  },
  cellRenderer: NumberRenderer,
  colId: 'newSharesPurchased',
};
const newNoOfSharesCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'New Shares Issued',
  field: 'noOfShares',
  cellRenderer: NumberRenderer,
};
const currencyCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Currency',
  valueGetter: (p: ValueGetterParams<TransactionHistoryTableData>) => p.data?.currency?.symbol,
  cellRenderer: SymbolRenderer,
  colId: 'currency',
};
const newlyDilutedSharesCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'New Fully Diluted Shares',
  field: 'fullyDilutedShares',
  cellRenderer: NumberRenderer,
};
const postMoneyValuationCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Post Money Valuation',
  field: 'postMoneyValuation',
  cellRenderer: (p: RowParams) => <CurrencyRenderer value={p.value} currencyCode={p.data?.currency?.code} />,
};
const valuationDateCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Valuation Date',
  valueGetter: (p: ValueGetterParams<TransactionHistoryTableData>) => dateAccessor(p.data?.valuationDate),
  cellRenderer: DateFormatter,
  colId: 'valuationDate',
};
const principalAmountCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Principal',
  field: 'principalAmount',
  cellRenderer: (p: RowParams) => <CurrencyRenderer value={p.value} currencyCode={p.data?.currency?.code} />,
};
const couponCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Coupon',
  field: 'coupon',
  cellRenderer: PercentageFormatter,
};
const maturityDateCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Maturity Date',
  valueGetter: (p: ValueGetterParams<TransactionHistoryTableData>) => dateAccessor(p.data?.maturityDate),
  cellRenderer: DateFormatter,
  colId: 'maturityDate',
};
const interestTypeCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Interest Type',
  field: 'couponType',
};
const compoundingFrequencyCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Compounding period',
  field: 'compoundingFrequency',
};
const valuationCapCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Valuation Cap',
  field: 'valuationCap',
  cellRenderer: (p: RowParams) => <CurrencyRenderer value={p.value} currencyCode={p.data?.currency?.code} />,
};
const discountRateCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Discount Rate',
  field: 'discountRate',
  cellRenderer: PercentageFormatter,
};
const triggerPriceCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Trigger Price',
  field: 'triggerPrice',
  cellRenderer: (p: RowParams) => <CurrencyRenderer value={p.value} currencyCode={p.data?.currency?.code} />,
};
const pikStartDateCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'PIK Start Date',
  valueGetter: (p: ValueGetterParams<TransactionHistoryTableData>) => dateAccessor(p.data?.pikStartDate),
  cellRenderer: DateFormatter,
  colId: 'pikStartDate',
};
const pikEndDateCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'PIK End Date',
  valueGetter: (p: ValueGetterParams<TransactionHistoryTableData>) => dateAccessor(p.data?.pikEndDate),
  cellRenderer: DateFormatter,
  colId: 'pikEndDate',
};
const pikCadenceCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'PIK Cadence Date',
  valueGetter: (p: ValueGetterParams<TransactionHistoryTableData>) => dateAccessor(p.data?.pikCadence),
  cellRenderer: DateFormatter,
  colId: 'pikCadence',
};
const transactionStatusCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Transaction Status',
  field: 'status',
};
const escrowAmountCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Escrow Amount',
  field: 'escrowAmount',
  cellRenderer: (p: RowParams) => <CurrencyRenderer value={p.value} currencyCode={p.data?.currency?.code} />,
};
const convertedSharesNoCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Number of Converted Shares',
  field: 'convertedSharesNo',
  cellRenderer: NumberRenderer,
};
const convertedPrincipalAmountCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Converted Principal Amount',
  field: 'convertedPrincipalAmount',
  cellRenderer: (p: RowParams) => <CurrencyRenderer value={p.value} currencyCode={p.data?.currency?.code} />,
};
const convertedInterestAmountCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Converted Interest',
  field: 'convertedInterestAmount',
  cellRenderer: (p: RowParams) => <CurrencyRenderer value={p.value} currencyCode={p.data?.currency?.code} />,
};
const fullyDilutedSharesCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'New Fully Diluted Shares',
  field: 'fullyDilutedShares',
  cellRenderer: NumberRenderer,
};
const miscPaymentAmountCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Amount',
  field: 'miscPaymentAmount',
  cellRenderer: (p: RowParams) => <CurrencyRenderer value={p.value} currencyCode={p.data?.currency?.code} />,
};
const miscPaymentTypeCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Payment Type',
  field: 'miscPaymentType',
};
const exchangedRoundCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Exchanged Stage',
  field: 'exchangedRound',
  cellRenderer: RoundRenderer,
  colId: 'exchangedRound',
};
const receivedRoundCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Received Stage',
  field: 'receivedRound',
  cellRenderer: RoundRenderer,
  colId: 'receivedRound',
};
const exchangedSharesNoCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Exchanged Shares',
  field: 'exchangedSharesNo',
  cellRenderer: NumberRenderer,
};
const receivedSharesNoCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Received Shares',
  field: 'receivedSharesNo',
  cellRenderer: NumberRenderer,
};
const warrantsIssuedCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Warrants Issued',
  field: 'warrantsIssued',
  cellRenderer: NumberRenderer,
};
const warrantExpiryDateCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Expiration Date',
  valueGetter: (p: ValueGetterParams<TransactionHistoryTableData>) => dateAccessor(p.data?.warrantExpiryDate),
  cellRenderer: DateFormatter,
  colId: 'warrantExpiryDate',
};
const exercisedWarrantsNoCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Warrants Exercised',
  field: 'exercisedWarrantsNo',
  cellRenderer: NumberRenderer,
};
const warrantVestedCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Vested',
  field: 'warrantVested',
  cellRenderer: BooleanRenderer,
};
const linkedNoteIdCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Note Name',
  field: 'linkedNoteId',
};
const interestPaymentAmountCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Interest Payment Amount',
  field: 'interestPaymentAmount',
  cellRenderer: (p: RowParams) => <CurrencyRenderer value={p.value} currencyCode={p.data?.currency?.code} />,
};
const optionsIssuedNoCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Options Issued',
  field: 'optionsIssuedNo',
  cellRenderer: NumberRenderer,
};
const optionsStrikePriceCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Strike Price',
  field: 'optionsStrikePrice',
  cellRenderer: (p: RowParams) => <CurrencyRenderer value={p.value} currencyCode={p.data?.currency?.code} />,
};
const optionsExpiryDateCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Expiration Date',
  valueGetter: (p: ValueGetterParams<TransactionHistoryTableData>) => dateAccessor(p.data?.optionsExpiryDate),
  cellRenderer: DateFormatter,
  colId: 'optionsExpiryDate',
};
const optionsExercisedNoCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Options Exercised',
  field: 'optionsExercisedNo',
  cellRenderer: NumberRenderer,
};
const valuationAmountCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Change in Unrealized',
  field: 'valuationAmount',
  cellRenderer: (p: RowParams) => <CurrencyRenderer value={p.value} currencyCode={p.data?.currency?.code} />,
};
const investmentAmountCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Amount',
  valueGetter: (p: ValueGetterParams<TransactionHistoryTableData>) => {
    if (!p.data) {
      return null;
    }
    return isITransactionDataModel(p.data) ? p.data.amountNormalized : p.data.investmentAmount;
  },
  cellRenderer: (p: RowParams) => <CurrencyRenderer value={p.value} currencyCode={p.data?.currency?.code} />,
  colId: 'investmentAmount',
};
const exchangedAmountCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Exchanged Amount',
  field: 'exchangedAmount',
  cellRenderer: (p: RowParams) => <CurrencyRenderer value={p.value} currencyCode={p.data?.currency?.code} />,
};
const receivedAmountCol: ColDef<TransactionHistoryTableData> = {
  headerName: 'Received Amount',
  field: 'receivedAmount',
  cellRenderer: (p: RowParams) => <CurrencyRenderer value={p.value} currencyCode={p.data?.currency?.code} />,
};

export function useTransactionHistoryColumns() {
  const usersByEmailMap = useAtomValue(usersByEmailMapAtom);
  const transactionTypesMap = useAtomValue(transactionTypesByIdMapAtom);

  const editorCol: ColDef<TransactionHistoryTableData> = useMemo(
    () => ({
      headerName: 'Editor',
      valueGetter: (p: ValueGetterParams<TransactionHistoryTableData>) => {
        if (!p.data?.createdBy) {
          return '';
        }
        return usersByEmailMap.get(p.data.createdBy)?.name ?? '';
      },
      cellRenderer: LongTextCellRenderer,
      width: 300,
      colId: 'editor',
    }),
    [usersByEmailMap]
  );

  const equityBuyCols = useMemo(
    () => [
      fundCol,
      transactionDateCol,
      roundCol,
      PPSCol,
      investmentAmountCol,
      newSharesPurchasedCol,
      currencyCol,
      newlyDilutedSharesCol,
      postMoneyValuationCol,
      transactionStatusCol,
      editorCol,
    ],
    [editorCol]
  );

  const equitySellCols = useMemo(
    () => [
      fundCol,
      transactionDateCol,
      escrowAmountCol,
      PPSCol,
      currencyCol,
      transactionStatusCol,
      editorCol,
    ],
    [editorCol]
  );

  const internalValuationCols = useMemo(
    () => [fundCol, valuationDateCol, PPSCol, valuationAmountCol, transactionStatusCol, editorCol],
    [editorCol]
  );

  const noteIssuanceCols = useMemo(
    () => [
      fundCol,
      transactionDateCol,
      principalAmountCol,
      couponCol,
      maturityDateCol,
      currencyCol,
      interestTypeCol,
      compoundingFrequencyCol,
      valuationCapCol,
      discountRateCol,
      triggerPriceCol,
      pikStartDateCol,
      pikEndDateCol,
      pikCadenceCol,
      transactionStatusCol,
      editorCol,
    ],
    [editorCol]
  );

  const noteConversionCols = useMemo(
    () => [
      fundCol,
      transactionDateCol,
      convertedRoundCol,
      convertedSharesNoCol,
      convertedPrincipalAmountCol,
      convertedInterestAmountCol,
      currencyCol,
      fullyDilutedSharesCol,
      postMoneyValuationCol,
      transactionStatusCol,
      editorCol,
    ],
    [editorCol]
  );

  const safeIssuanceCols = useMemo(
    () => [
      fundCol,
      transactionDateCol,
      principalAmountCol,
      maturityDateCol,
      currencyCol,
      triggerPriceCol,
      valuationCapCol,
      discountRateCol,
      transactionStatusCol,
      editorCol,
    ],
    [editorCol]
  );

  const safeConversionCols = useMemo(
    () => [
      transactionDateCol,
      fundCol,
      convertedRoundCol,
      convertedPrincipalAmountCol,
      convertedSharesNoCol,
      currencyCol,
      fullyDilutedSharesCol,
      postMoneyValuationCol,
      transactionStatusCol,
      editorCol,
    ],
    [editorCol]
  );

  const miscPaymentCols = useMemo(
    () => [
      fundCol,
      paymentDateCol,
      miscPaymentAmountCol,
      currencyCol,
      miscPaymentTypeCol,
      transactionStatusCol,
      editorCol,
    ],
    [editorCol]
  );

  const shareExchangeCols = useMemo(
    () => [
      fundCol,
      transactionDateCol,
      exchangedRoundCol,
      receivedRoundCol,
      exchangedSharesNoCol,
      receivedSharesNoCol,
      exchangedAmountCol,
      receivedAmountCol,
      currencyCol,
      fullyDilutedSharesCol,
      postMoneyValuationCol,
      transactionStatusCol,
      editorCol,
    ],
    [editorCol]
  );

  const stockIssuanceCols = useMemo(
    () => [
      fundCol,
      transactionDateCol,
      roundCol,
      currencyCol,
      postMoneyValuationCol,
      transactionStatusCol,
      editorCol,
    ],
    [editorCol]
  );

  const warrantIssuanceCols = useMemo(
    () => [
      fundCol,
      transactionDateCol,
      currencyCol,
      warrantsIssuedCol,
      warrantExpiryDateCol,
      transactionStatusCol,
      editorCol,
    ],
    [editorCol]
  );

  const warrantExerciseCols = useMemo(
    () => [
      fundCol,
      transactionDateCol,
      currencyCol,
      exercisedWarrantsNoCol,
      warrantVestedCol,
      transactionStatusCol,
      editorCol,
    ],
    [editorCol]
  );

  const interestPaymentCols = useMemo(
    () => [
      fundCol,
      transactionDateCol,
      currencyCol,
      linkedNoteIdCol,
      interestPaymentAmountCol,
      transactionStatusCol,
      editorCol,
    ],
    [editorCol]
  );

  const secondaryPurchaseCols = useMemo(
    () => [
      fundCol,
      transactionDateCol,
      roundCol,
      PPSCol,
      newNoOfSharesCol,
      currencyCol,
      fullyDilutedSharesCol,
      postMoneyValuationCol,
      transactionStatusCol,
      editorCol,
    ],
    [editorCol]
  );

  const noteExpirationCols = useMemo(
    () => [fundCol, transactionDateCol, transactionStatusCol, editorCol],
    [editorCol]
  );
  const safeExpirationCols = noteExpirationCols;
  const warrantExpirationCols = noteExpirationCols;
  const optionExpirationCols = noteExpirationCols;

  const optionIssuanceCols = useMemo(
    () => [
      fundCol,
      transactionDateCol,
      currencyCol,
      optionsIssuedNoCol,
      optionsStrikePriceCol,
      optionsExpiryDateCol,
      transactionStatusCol,
      editorCol,
    ],
    [editorCol]
  );

  const optionExerciseCols = useMemo(
    () => [fundCol, transactionDateCol, currencyCol, optionsExercisedNoCol, transactionStatusCol, editorCol],
    [editorCol]
  );

  const defaultCols: ColDef<TransactionHistoryTableData>[] = useMemo(
    () => [fundCol, transactionDateCol, transactionStatusCol, editorCol],
    [editorCol]
  );

  const columnsByTransactionType: Record<string, ColDef<TransactionHistoryTableData>[]> = useMemo(
    () => ({
      Buy: equityBuyCols,
      Sell: equitySellCols,
      'Internal Valuation': internalValuationCols,
      'Note Issuance': noteIssuanceCols,
      'Note Conversion': noteConversionCols,
      SAFE: safeIssuanceCols,
      'SAFE Conversion': safeConversionCols,
      'Misc Payment': miscPaymentCols,
      'Share Exchange': shareExchangeCols,
      'Stock Issuance': stockIssuanceCols,
      'Warrant Issuance': warrantIssuanceCols,
      'Warrant Exercise': warrantExerciseCols,
      'Interest Payment': interestPaymentCols,
      'Secondary Purchase': secondaryPurchaseCols,
      'Note Expiration': noteExpirationCols,
      'SAFE Expiration': safeExpirationCols,
      'Warrant Expiration': warrantExpirationCols,
      'Option Issuance': optionIssuanceCols,
      'Option Exercise': optionExerciseCols,
      'Option Expiration': optionExpirationCols,
      default: defaultCols,
    }),
    [
      defaultCols,
      equityBuyCols,
      equitySellCols,
      interestPaymentCols,
      internalValuationCols,
      miscPaymentCols,
      noteConversionCols,
      noteExpirationCols,
      noteIssuanceCols,
      optionExerciseCols,
      optionExpirationCols,
      optionIssuanceCols,
      safeConversionCols,
      safeExpirationCols,
      safeIssuanceCols,
      secondaryPurchaseCols,
      shareExchangeCols,
      stockIssuanceCols,
      warrantExerciseCols,
      warrantExpirationCols,
      warrantIssuanceCols,
    ]
  );

  const getTransactionHistoryColumns = useCallback(
    ({ transactionTypeId }: ITransactionDataModel) => {
      const transactionType = transactionTypesMap.get(transactionTypeId) as TransactionType;
      return columnsByTransactionType[transactionType.name] || columnsByTransactionType.default;
    },
    [columnsByTransactionType, transactionTypesMap]
  );

  return getTransactionHistoryColumns;
}
